import {
  ArrowRight,
  Bookmark,
  CalendarDays,
  Eye,
  Inbox,
  Layers3,
  ShieldCheck,
} from "lucide-react";
import type { ComponentType, PropsWithChildren, ReactNode } from "react";
import { Link, type MetaFunction } from "react-router";

import { LogoLockup } from "@/site/ui/logo";
import { ThemeControl } from "@/theme/theme-control";
import { Button } from "@/ui/button";

import { PricingPlans } from "./pricing-plans";

export const meta: MetaFunction = () => [
  { title: `Pricing · ${publicEnv.siteTitle}` },
  {
    name: "description",
    content:
      "Choose a Bookmarked FYI curator plan for saving public X posts privately and publishing deliberate collections.",
  },
];

// Explain the curator subscription and render the live Stripe price catalog.
//
export default function Pricing() {
  const trialDays = publicEnv.stripeTrialDays;

  return (
    <div className="min-h-dvh overflow-hidden">
      <div
        aria-hidden="true"
        className="bookmark-grid pointer-events-none absolute inset-x-0 top-0 -z-20 h-[44rem] opacity-75 [mask-image:linear-gradient(to_bottom,black,transparent)]"
      />

      <header className="bg-background/85 sticky top-0 z-50 border-b backdrop-blur-xl">
        <div className="mx-auto flex h-17 w-[calc(100%-2rem)] max-w-7xl items-center justify-between sm:w-[calc(100%-3rem)]">
          <LogoLockup linkTo="/" />
          <nav
            aria-label="Pricing navigation"
            className="text-muted-foreground hidden items-center gap-7 text-sm md:flex"
          >
            <Link className="hover:text-foreground" to="/">
              Home
            </Link>
            <a className="hover:text-foreground" href="#plans">
              Plans
            </a>
            <a className="hover:text-foreground" href="#faq">
              FAQ
            </a>
          </nav>
          <div className="flex items-center gap-1.5">
            <ThemeControl />
            <Button asChild className="rounded-full px-4" size="sm">
              <Link to="/studio">
                Open Studio
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <section className="mx-auto grid w-[calc(100%-2rem)] max-w-7xl gap-12 pt-18 pb-20 sm:w-[calc(100%-3rem)] sm:pt-24 sm:pb-28 lg:grid-cols-[1fr_20rem] lg:items-end">
          <div className="max-w-4xl">
            <p className="border-signal/30 bg-signal/10 text-signal-strong inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-bold tracking-[0.1em] uppercase">
              Simple curator pricing
            </p>
            <h1 className="font-display mt-7 text-5xl leading-[0.94] font-semibold tracking-[-0.06em] text-balance sm:text-7xl lg:text-[5.2rem]">
              One private library. Collections worth sharing.
            </h1>
            <p className="text-muted-foreground mt-7 max-w-2xl text-lg leading-8 sm:text-xl">
              Every plan includes the complete curator Studio. Choose the
              billing rhythm that fits, then save privately and publish only
              what is ready.
            </p>
          </div>

          <aside className="bg-card rounded-[2rem] border p-6 shadow-sm">
            <CalendarDays className="text-signal-strong size-5" />
            <p className="font-display mt-4 text-2xl font-semibold">
              {trialDays > 0 ? `${trialDays} days free` : "Start when ready"}
            </p>
            <p className="text-muted-foreground mt-2 text-sm leading-6">
              {trialDays > 0
                ? "Use every Studio feature before your first payment. No card is required to begin."
                : "Choose an available plan and manage it anytime from Billing."}
            </p>
          </aside>
        </section>

        <section
          aria-labelledby="plans-title"
          className="mx-auto w-[calc(100%-2rem)] max-w-7xl scroll-mt-24 pb-24 sm:w-[calc(100%-3rem)] sm:pb-32"
          id="plans"
        >
          <div className="grid items-end gap-6 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="text-signal-strong text-xs font-bold tracking-[0.13em] uppercase">
                Choose your plan
              </p>
              <h2
                className="font-display mt-4 text-4xl leading-tight font-semibold tracking-[-0.05em] sm:text-6xl"
                id="plans-title"
              >
                Same Studio. Your schedule.
              </h2>
            </div>
            <p className="text-muted-foreground max-w-xl leading-7 lg:justify-self-end">
              Plan selection is carried through X sign-in, so a new curator
              starts on the price chosen here. Plans can be changed later from
              Billing.
            </p>
          </div>

          <PricingPlans className="mt-12" />
          <p className="text-muted-foreground mt-4 text-center text-xs">
            Prices are shown in the catalog currency. Payments are securely
            handled by Stripe.
          </p>
        </section>

        <section className="bg-foreground text-background">
          <div className="mx-auto w-[calc(100%-2rem)] max-w-7xl py-20 sm:w-[calc(100%-3rem)] sm:py-28">
            <p className="text-signal text-xs font-bold tracking-[0.13em] uppercase">
              Included in every plan
            </p>
            <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              <IncludedFeature icon={ShieldCheck} title="Verified identity">
                Anchor your curator profile to the X account and current handle
                that control it.
              </IncludedFeature>
              <IncludedFeature icon={Inbox} title="Private capture">
                Keep pasted public post links in a private Inbox until you know
                where they belong.
              </IncludedFeature>
              <IncludedFeature icon={Layers3} title="Deliberate collections">
                Add titles, context, and sequence before choosing to publish a
                collection.
              </IncludedFeature>
              <IncludedFeature icon={Eye} title="Public profile">
                Share the collections you publish while keeping everything else
                private.
              </IncludedFeature>
            </div>
          </div>
        </section>

        <section
          className="mx-auto grid w-[calc(100%-2rem)] max-w-7xl scroll-mt-24 gap-12 py-24 sm:w-[calc(100%-3rem)] sm:py-32 lg:grid-cols-[0.75fr_1.25fr]"
          id="faq"
        >
          <div>
            <p className="text-signal-strong text-xs font-bold tracking-[0.13em] uppercase">
              Pricing FAQ
            </p>
            <h2 className="font-display mt-4 text-4xl font-semibold tracking-[-0.05em] sm:text-6xl">
              The practical details.
            </h2>
          </div>

          <div className="divide-y border-y">
            {trialDays > 0 && (
              <PricingQuestion question="Do I need a card for the free trial?">
                No. Start the {trialDays}-day trial through X sign-in, then add
                a payment method from Billing before the trial ends.
              </PricingQuestion>
            )}
            <PricingQuestion question="What happens if my subscription ends?">
              Studio editing pauses, but your saved library remains stored and
              published collections stay available while you resolve billing.
            </PricingQuestion>
            <PricingQuestion question="Can I change plans or cancel?">
              Yes. Switch among available plans, update the payment method, or
              manage cancellation from Billing.
            </PricingQuestion>
            <PricingQuestion question="Does Bookmarked import my X bookmarks?">
              No. Sign-in verifies your handle. Bookmarked stores only the
              public post links you deliberately paste into Studio.
            </PricingQuestion>
          </div>
        </section>

        <section className="bg-signal/10 mx-auto mb-24 w-[calc(100%-2rem)] max-w-7xl rounded-[2rem] border px-6 py-14 text-center sm:w-[calc(100%-3rem)] sm:px-10 sm:py-18">
          <Bookmark className="text-signal-strong mx-auto size-7" />
          <h2 className="font-display mx-auto mt-5 max-w-3xl text-4xl font-semibold tracking-[-0.05em] sm:text-6xl">
            Turn the links you keep into something others can follow.
          </h2>
          <Button asChild className="mt-8 h-12 rounded-full px-6 text-base">
            <a href="#plans">
              Choose a plan
              <ArrowRight />
            </a>
          </Button>
        </section>
      </main>

      <footer className="border-t">
        <div className="text-muted-foreground mx-auto flex w-[calc(100%-2rem)] max-w-7xl flex-col gap-5 py-8 text-sm sm:w-[calc(100%-3rem)] sm:flex-row sm:items-center sm:justify-between">
          <LogoLockup className="text-foreground" linkTo="/" />
          <nav aria-label="Legal" className="flex flex-wrap gap-x-5 gap-y-2">
            <Link className="hover:text-foreground" to="/">
              Home
            </Link>
            <Link className="hover:text-foreground" to="/terms">
              Terms
            </Link>
            <Link className="hover:text-foreground" to="/privacy">
              Privacy
            </Link>
          </nav>
          <p>© {new Date().getFullYear()} Bookmarked FYI</p>
        </div>
      </footer>
    </div>
  );
}

interface IncludedFeatureProps {
  icon: ComponentType<{ className?: string }>;
  title: string;
}

// Summarize one curator capability included with every subscription.
//
function IncludedFeature({
  children,
  icon: Icon,
  title,
}: PropsWithChildren<IncludedFeatureProps>) {
  return (
    <article>
      <span className="flex size-11 items-center justify-center rounded-2xl bg-white/8">
        <Icon className="text-signal size-5" />
      </span>
      <h3 className="font-display mt-5 text-xl font-semibold">{title}</h3>
      <p className="mt-2 text-sm leading-6 opacity-65">{children}</p>
    </article>
  );
}

interface PricingQuestionProps {
  children: ReactNode;
  question: string;
}

// Reveal one pricing answer without making the page visually dense.
//
function PricingQuestion({ children, question }: PricingQuestionProps) {
  return (
    <details className="group py-5">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium">
        {question}
        <span className="text-signal-strong text-xl transition-transform group-open:rotate-45">
          +
        </span>
      </summary>
      <p className="text-muted-foreground max-w-2xl pt-3 text-sm leading-7">
        {children}
      </p>
    </details>
  );
}
